import { SectionCard } from "#src/commonV2/SectionCard";
import useFetchNoPaging from "#src/hooks/useFetchNoPaging";
import dlpService from "#src/services/endpoint/dlp";
import { Edit02, Plus, Tag01 } from "@untitled-ui/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SensitivityLabel } from "../Sensitivity";
import { LoaderV2, LoadingStateV2 } from "#src/commonV2/LoadingState";
import { BadgeV2 } from "#src/commonV2/badges/Badge";
import { Switch, Tooltip } from "@lockerpm/design";
import { EditContentTagModal } from "./EditContentModal";
import { fileSensitivityItems } from "#src/components/endpoint/devices/details/drive/filterItems";
import { TAILWIND_COLORS } from "#src/utils/common";
import useFetch from "#src/hooks/useFetch";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";

interface IEndpointDlpDataLabelManageSectionProps {
  workspaceId: string;
}

export const EndpointDlpDataLabelManageSection = ({
  workspaceId,
}: IEndpointDlpDataLabelManageSectionProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("endpoint", {
    keyPrefix: "dlp.dataLabeling.dataLabel",
  });

  // --------------- STATES ---------------

  const [openContentModal, setOpenContentModal] = useState<boolean>(false);

  // --------------- HOOKS ---------------

  const fetchParams = useMemo<[workspaceId: string]>(
    () => [workspaceId],
    [workspaceId]
  );

  const { list: sensitivityList, isLoading: isLoadingSensitivity } =
    useFetchNoPaging(dlpService.listDataLabelSensitivity, fetchParams);

  const {
    list: contentList,
    isLoading: isLoadingContent,
    updateData: updateContentList,
  } = useFetchNoPaging(dlpService.listDataLabelContent, fetchParams);

  const { result: requireExplain, updateData: updateExplain } = useFetch(
    dlpService.retrieveConfigSensitivityRequest,
    fetchParams
  );

  // --------------- COMPUTED ---------------

  const sortedSensitivityList = useMemo(() => {
    if (sensitivityList === null) return [];
    return sensitivityList.sort(
      (a, b) =>
        fileSensitivityItems.findIndex((item) => item.value === a.id) -
        fileSensitivityItems.findIndex((item) => item.value === b.id)
    );
  }, [sensitivityList]);

  // --------------- METHODS ---------------

  const onToggleRequireExplain = (willBeEnable: boolean) => {
    dlpService
      .updateConfigSensitivityRequest(workspaceId, willBeEnable)
      .then((response) => {
        console.log(response);
        updateExplain();
      })
      .catch(apiErrorHandler);
  };

  // --------------- RENDER ---------------

  return (
    <>
      <SectionCard
        header={
          <div className="flex items-center gap-2">
            <Tag01 className="h-5 w-5 min-w-5 text-gray-v2" />
            {t("title")}
          </div>
        }
      >
        <div className="px-11 py-5 flex flex-col gap-5 border border-gray-v2-200 bg-white rounded-xl">
          {sensitivityList === null ||
          contentList === null ||
          isLoadingSensitivity ||
          isLoadingContent ? (
            <LoadingStateV2 />
          ) : (
            <>
              <span className="text-md text-gray-v2-600">{t("hint")}</span>
              <div className="flex flex-col gap-3">
                <span className="text-md font-semibold text-gray-v2-900">
                  {t("sensitivity")}
                </span>
                <div className="flex gap-1">
                  {sortedSensitivityList.map((item) => {
                    const tooltipText =
                      language === "vi"
                        ? item.description.vi
                        : item.description.en;

                    return (
                      <Tooltip
                        key={item.id}
                        color={TAILWIND_COLORS["gray-v2-900"]}
                        title={
                          tooltipText ? (
                            <p className="text-xs text-white font-semibold text-center">
                              {tooltipText}
                            </p>
                          ) : null
                        }
                      >
                        {/* Need this empty div since Antd Tooltip can't catch custom component */}
                        <div>
                          <SensitivityLabel
                            sensitivity={{ id: item.id, name: item.name }}
                          />
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <span className="text-md font-semibold text-gray-v2-900">
                  {t("content.title")}
                </span>
                <div className="flex items-center gap-1">
                  {contentList.length > 0 ? (
                    contentList.map((item) => (
                      <BadgeV2 key={item.id} name={item.name} defaultIcon />
                    ))
                  ) : (
                    <span className="text-md text-gray-v2-600">
                      {t("content.noTags")}
                    </span>
                  )}
                  <button
                    className="px-[0.1875rem] py-0.5 flex items-center rounded-2xl bg-gray-v2-50 border border-gray-v2-200"
                    onClick={() => {
                      setOpenContentModal(true);
                    }}
                  >
                    {contentList.length > 0 ? (
                      <Edit02 className="h-4 w-4 min-w-4 text-gray-v2" />
                    ) : (
                      <Plus className="h-4 w-4 min-w-4 text-gray-v2" />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {requireExplain === null ? (
                  <LoaderV2 baseSize={1} />
                ) : (
                  <Switch
                    checked={requireExplain.enable}
                    onClick={onToggleRequireExplain}
                  />
                )}
                <span className="text-sm font-medium text-gray-v2-900">
                  {t("requestExplanation")}
                </span>
              </div>
            </>
          )}
        </div>
      </SectionCard>
      <EditContentTagModal
        workspaceId={workspaceId}
        open={openContentModal}
        onClose={() => {
          setOpenContentModal(false);
        }}
        onRefresh={updateContentList}
        savedTags={
          contentList
            ? contentList.map((tag) => ({
                id: tag.id.toString(),
                name: tag.name,
              }))
            : []
        }
      />
    </>
  );
};
